import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, BookOpen, TrendingUp, AlertTriangle, Plus, ChevronRight, Clock, Target, BarChart3, RefreshCw } from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const TeacherDashboard = () => {
  const [teacher, setTeacher] = useState(null);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeacherData();
  }, []);

  const getTeacherToken = () => localStorage.getItem("teacherToken");

  const fetchTeacherData = async () => {
    const token = getTeacherToken();
    if (!token) {
      navigate("/teacher/login");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [teacherRes, classesRes, assignmentsRes] = await Promise.all([
        fetch(`${API_URL}/teacher/me`, { headers }),
        fetch(`${API_URL}/teacher/classes`, { headers }),
        fetch(`${API_URL}/teacher/assignments`, { headers })
      ]);

      if (!teacherRes.ok) {
        navigate("/teacher/login");
        return;
      }

      const teacherData = await teacherRes.json();
      const classesData = await classesRes.json();
      const assignmentsData = await assignmentsRes.json();

      setTeacher(teacherData);
      setClasses(classesData);
      setAssignments(assignmentsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalStudents = () => classes.reduce((sum, c) => sum + (c.student_count || 0), 0);
  
  const getActiveAssignments = () => assignments.filter(a => a.status === "active").length;
  
  const getAtRiskCount = () => {
    // This would come from roster data in a real implementation
    return assignments.reduce((sum, a) => {
      const stats = a.completion_stats || {};
      return sum + (stats.total - stats.completed);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-bold text-2xl">Teacher Dashboard</h1>
              <p className="text-indigo-200 mt-1">Welcome back, {teacher?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/teacher/classes/new"
                className="px-4 py-2 bg-white/20 text-white font-medium rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Class
              </Link>
              <Link
                to="/teacher/assignments/new"
                className="px-4 py-2 bg-lime-500 text-white font-medium rounded-lg hover:bg-lime-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Assignment
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border-2 border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Total Students</p>
                <p className="font-heading font-bold text-2xl text-slate-800">{getTotalStudents()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-lime-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Classes</p>
                <p className="font-heading font-bold text-2xl text-slate-800">{classes.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Active Assignments</p>
                <p className="font-heading font-bold text-2xl text-slate-800">{getActiveAssignments()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-red-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Need Attention</p>
                <p className="font-heading font-bold text-2xl text-red-600">{getAtRiskCount()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Classes Section */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-slate-800">Your Classes</h2>
              <Link to="/teacher/classes" className="text-indigo-600 text-sm font-medium hover:underline">
                View All
              </Link>
            </div>
            <div className="divide-y divide-slate-50">
              {classes.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No classes yet</p>
                  <Link
                    to="/teacher/classes/new"
                    className="text-indigo-600 font-medium hover:underline mt-2 inline-block"
                  >
                    Create your first class
                  </Link>
                </div>
              ) : (
                classes.slice(0, 5).map((cls) => (
                  <Link
                    key={cls.id}
                    to={`/teacher/classes/${cls.id}`}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-800">{cls.name}</h3>
                        <p className="text-sm text-slate-500">
                          {cls.student_count} students • Grade {cls.grade_level}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Recent Assignments */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-slate-800">Recent Assignments</h2>
              <Link to="/teacher/assignments" className="text-indigo-600 text-sm font-medium hover:underline">
                View All
              </Link>
            </div>
            <div className="divide-y divide-slate-50">
              {assignments.length === 0 ? (
                <div className="p-8 text-center">
                  <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No assignments yet</p>
                  <Link
                    to="/teacher/assignments/new"
                    className="text-indigo-600 font-medium hover:underline mt-2 inline-block"
                  >
                    Create your first assignment
                  </Link>
                </div>
              ) : (
                assignments.slice(0, 5).map((assignment) => (
                  <Link
                    key={assignment.id}
                    to={`/teacher/assignments/${assignment.id}`}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Target className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-800">{assignment.title}</h3>
                        <p className="text-sm text-slate-500">
                          {assignment.class_name} • {assignment.completion_stats?.completed || 0}/{assignment.completion_stats?.total || 0} completed
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-medium ${
                        assignment.completion_stats?.completion_rate >= 80 ? 'text-green-600' :
                        assignment.completion_stats?.completion_rate >= 50 ? 'text-orange-600' :
                        'text-red-600'
                      }`}>
                        {assignment.completion_stats?.completion_rate || 0}%
                      </span>
                      <p className="text-xs text-slate-400">
                        Avg: {assignment.completion_stats?.avg_score || 0}%
                      </p>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <Link
            to="/teacher/roster"
            className="bg-white rounded-xl p-5 border-2 border-slate-100 hover:border-indigo-200 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-800">View All Students</h3>
                <p className="text-sm text-slate-500">See roster & at-risk list</p>
              </div>
            </div>
          </Link>

          <Link
            to="/teacher/analytics"
            className="bg-white rounded-xl p-5 border-2 border-slate-100 hover:border-indigo-200 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center group-hover:bg-lime-200 transition-colors">
                <BarChart3 className="w-5 h-5 text-lime-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-800">Class Analytics</h3>
                <p className="text-sm text-slate-500">Weak topics & trends</p>
              </div>
            </div>
          </Link>

          <Link
            to="/teacher/reteach"
            className="bg-white rounded-xl p-5 border-2 border-slate-100 hover:border-indigo-200 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-800">Reteach Packs</h3>
                <p className="text-sm text-slate-500">Generate remediation sets</p>
              </div>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TeacherDashboard;
